import type { MailboxAdapter, EmailSummary, CalendarEvent } from "./mailbox-adapter";
import { debugLog } from "./debug";

/** A tally of one Outlook category across the recent inbox slice. */
export interface CategoryCount {
  name: string;
  count: number;
}

/** Aggregated, display-ready mailbox overview computed by `loadDashboard`. */
export interface DashboardData {
  unreadCount: number;
  needsReply: EmailSummary[];
  meetings: CalendarEvent[];
  categories: CategoryCount[];
  meetingsUnavailable: boolean;
}

export const NEEDS_REPLY_CAP = 10;
export const CATEGORY_SCAN_CAP = 50;
export const UNCATEGORIZED = "Uncategorized";

const CALENDAR_REQUIRES_GRAPH_MESSAGE = "Calendar requires Graph (Exchange Online).";

/** Formats an ISO timestamp as a locale HH:MM string; returns "" for an invalid/empty input. */
export function formatTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function todayBounds(): { fromIso: string; toIso: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return { fromIso: start.toISOString(), toIso: end.toISOString() };
}

function tallyCategories(emails: EmailSummary[]): CategoryCount[] {
  const counts = new Map<string, number>();
  for (const email of emails.slice(0, CATEGORY_SCAN_CAP)) {
    const names = email.categories.length > 0 ? email.categories : [UNCATEGORIZED];
    for (const name of names) {
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
  }
  return Array.from(counts, ([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
}

/**
 * Aggregates a read-only mailbox overview from the adapter. Each metric is read
 * independently via `Promise.allSettled` so one failing read degrades to its
 * empty value instead of blanking the whole dashboard. Failures are logged, not
 * thrown — the dashboard is a convenience surface, never critical state.
 */
export async function loadDashboard(adapter: MailboxAdapter): Promise<DashboardData> {
  const { fromIso, toIso } = todayBounds();
  const [unread, needsReply, meetings, recent] = await Promise.allSettled([
    adapter.listEmails("unread"),
    adapter.listEmails("needs_reply"),
    adapter.listCalendarEvents(fromIso, toIso),
    adapter.listEmails("recent"),
  ]);

  let unreadCount = 0;
  if (unread.status === "fulfilled") unreadCount = unread.value.length;
  else debugLog("dashboard:unread-failed", { error: String(unread.reason) });

  let needsReplyList: EmailSummary[] = [];
  if (needsReply.status === "fulfilled")
    needsReplyList = needsReply.value.slice(0, NEEDS_REPLY_CAP);
  else debugLog("dashboard:needs-reply-failed", { error: String(needsReply.reason) });

  let meetingsList: CalendarEvent[] = [];
  let meetingsUnavailable = false;
  if (meetings.status === "fulfilled") {
    meetingsList = meetings.value;
  } else {
    const reason = String(meetings.reason);
    meetingsUnavailable = reason.includes(CALENDAR_REQUIRES_GRAPH_MESSAGE);
    debugLog("dashboard:meetings-failed", { error: reason });
  }

  let categories: CategoryCount[] = [];
  if (recent.status === "fulfilled") categories = tallyCategories(recent.value);
  else debugLog("dashboard:recent-failed", { error: String(recent.reason) });

  return {
    unreadCount,
    needsReply: needsReplyList,
    meetings: meetingsList,
    categories,
    meetingsUnavailable,
  };
}
